import { RUNS_ON_TEXT, type RunsOn, type ServiceStatus, type ServiceView, type Watcher } from "@pagerless/schema";
import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router";

/**
 * The services map: one lane per place services run (AWS ECS, Cloudflare, Scheduled jobs…), each
 * service a node in its status colour, and lines from the alarms and rules that watch a service to it.
 * Pure SVG and CSS. Lanes sit side by side, and stack when the map is narrower than STACK_BELOW.
 */

/** Plain words for a service's status: the same on the list, the map and the service screen. */
export const STATUS_TEXT: Record<ServiceStatus, string> = {
  failing: "Failing",
  silent: "Went quiet",
  ok: "OK",
  none: "No signal yet",
};

/** The status dot (colour is never the only signal: the text says it too). */
export function statusDot(s: ServiceStatus): string {
  return { failing: "dot down", silent: "dot quiet", ok: "dot up", none: "dot" }[s];
}

/** The small status label. */
export function statusLabel(s: ServiceStatus): string {
  return { failing: "st hot", silent: "st warn", ok: "st ok", none: "st" }[s];
}

/** Lanes left to right: where code runs first, then jobs, then what AWS itself tells us. */
export const LANE_ORDER: readonly RunsOn[] = [
  "ecs",
  "lambda",
  "cloudflare",
  "vercel",
  "railway",
  "server",
  "browser",
  "device",
  "jobs",
  "aws",
  "unknown",
];

export type Lane = { runsOn: RunsOn; title: string; services: ServiceView[] };

/** Services grouped by where they run, in LANE_ORDER; empty lanes are left out. Order within a lane is kept. */
export function mapLanes(services: ServiceView[]): Lane[] {
  return LANE_ORDER.map((runsOn) => ({
    runsOn,
    title: RUNS_ON_TEXT[runsOn],
    services: services.filter((s) => s.runsOn === runsOn),
  })).filter((l) => l.services.length > 0);
}

/** Watchers drawn on the map; the rest are counted ("+5 more") and listed on each service's screen. */
export const MAX_WATCHERS = 12;

export const watcherKey = (w: Watcher) => `${w.kind}:${w.name}`;
export const serviceKey = (s: Pick<ServiceView, "name">) => `svc:${s.name}`;

/** Distinct watchers, the ones watching a failing service first, then those watching the most services. */
export function mapWatchers(services: ServiceView[]): { shown: Watcher[]; more: number } {
  const all = new Map<string, { w: Watcher; n: number; hot: boolean }>();
  for (const s of services) {
    for (const w of s.watchedBy) {
      const k = watcherKey(w);
      const e = all.get(k) ?? { w, n: 0, hot: false };
      e.n += 1;
      e.hot ||= s.status === "failing";
      all.set(k, e);
    }
  }
  const sorted = [...all.values()].sort(
    (a, b) => Number(b.hot) - Number(a.hot) || b.n - a.n || a.w.name.localeCompare(b.w.name),
  );
  return { shown: sorted.slice(0, MAX_WATCHERS).map((e) => e.w), more: Math.max(0, sorted.length - MAX_WATCHERS) };
}

export type Edge = { from: string; to: string; hot: boolean };

/** One line per (shown watcher, service it watches). Lines to a failing service are hot. */
export function mapEdges(services: ServiceView[], shown: Watcher[]): Edge[] {
  const keep = new Set(shown.map(watcherKey));
  return services.flatMap((s) =>
    s.watchedBy
      .filter((w) => keep.has(watcherKey(w)))
      .map((w) => ({ from: watcherKey(w), to: serviceKey(s), hot: s.status === "failing" })),
  );
}

/** Below this width (px) lanes stack. */
export const STACK_BELOW = 640;

/** Unknown width (0, before the first measure) keeps lanes side by side. */
export function mapLayout(width: number): "lanes" | "stack" {
  return width > 0 && width < STACK_BELOW ? "stack" : "lanes";
}

type Box = { left: number; top: number; right: number; bottom: number };
type Pt = [number, number];

/** Half the gap between lanes: lines run down the middle of it. */
const GUTTER = 12;

/** A polyline with softly rounded corners. */
export function roundedPath(points: Pt[], r = 6): string {
  const pts = points.filter((p, i) => i === 0 || p[0] !== points[i - 1]?.[0] || p[1] !== points[i - 1]?.[1]);
  const [first, ...rest] = pts;
  if (!first) return "";
  let d = `M${first[0]},${first[1]}`;
  for (let i = 0; i < rest.length; i++) {
    const p = rest[i] as Pt;
    const prev = (pts[i] ?? first) as Pt;
    const next = rest[i + 1];
    if (!next) {
      d += ` L${p[0]},${p[1]}`;
      break;
    }
    const k = (from: Pt, to: Pt, by: number): Pt => {
      const len = Math.hypot(to[0] - from[0], to[1] - from[1]) || 1;
      return [from[0] + ((to[0] - from[0]) / len) * by, from[1] + ((to[1] - from[1]) / len) * by];
    };
    const inLen = Math.hypot(p[0] - prev[0], p[1] - prev[1]);
    const outLen = Math.hypot(next[0] - p[0], next[1] - p[1]);
    const c = Math.min(r, inLen / 2, outLen / 2);
    const a = k(p, prev, c);
    const b = k(p, next, c);
    d += ` L${a[0]},${a[1]} Q${p[0]},${p[1]} ${b[0]},${b[1]}`;
  }
  return d;
}

/**
 * From a watcher (left column) to a service. Next door: one soft curve. Further right: out into the
 * gutter, along the channel above the target's row (`channelY`), down the gutter beside its lane and
 * in from the left, so a line never runs through another service.
 */
export function edgePath(a: Box, b: Box, channelY: number): string {
  const ya = (a.top + a.bottom) / 2;
  const yb = (b.top + b.bottom) / 2;
  if (b.left - a.right <= 4 * GUTTER) {
    const mid = (a.right + b.left) / 2;
    return `M${a.right},${ya} C${mid},${ya} ${mid},${yb} ${b.left},${yb}`;
  }
  const gx = a.right + GUTTER;
  const lx = b.left - GUTTER;
  return roundedPath([
    [a.right, ya],
    [gx, ya],
    [gx, channelY],
    [lx, channelY],
    [lx, yb],
    [b.left, yb],
  ]);
}

/** Which nodes light up with the one under the pointer (or tapped): it, and everything it is joined to. */
export function litNodes(focus: string | null, edges: Edge[]): Set<string> {
  if (focus === null) return new Set();
  const lit = new Set([focus]);
  for (const e of edges) {
    if (e.from === focus) lit.add(e.to);
    if (e.to === focus) lit.add(e.from);
  }
  return lit;
}

export function ServicesMap({
  services,
  hrefOf,
  width,
}: {
  services: ServiceView[];
  hrefOf: (s: ServiceView) => string;
  /** Fixed width for tests; otherwise the map measures itself. */
  width?: number;
}) {
  const box = useRef<HTMLElement>(null);
  const nodes = useRef(new Map<string, HTMLElement>());
  const [measured, setMeasured] = useState(0);
  const [paths, setPaths] = useState<Array<Edge & { d: string }>>([]);
  const [hovered, setFocus] = useState<string | null>(null);
  // A tapped alarm stays lit (phones have no hover); hover wins while it lasts.
  const [pinned, setPinned] = useState<string | null>(null);
  const focus = hovered ?? pinned;

  const lanes = mapLanes(services);
  const { shown, more } = mapWatchers(services);
  const edges = mapEdges(services, shown);
  const layout = mapLayout(width ?? measured);
  const edgeKey = edges.map((e) => `${e.from}>${e.to}:${e.hot}`).join("|");
  const edgesRef = useRef(edges);
  edgesRef.current = edges;
  const widthRef = useRef(width);
  widthRef.current = width;
  const lit = litNodes(focus, edges);

  // Lines follow the nodes wherever the layout puts them: measure after layout and on every resize.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `edgeKey` and `layout` are what moves the lines.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const draw = () => {
      setMeasured(el.clientWidth);
      const o = el.getBoundingClientRect();
      const rel = (n: HTMLElement): Box | null => {
        const r = n.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) return null;
        return { left: r.left - o.left, top: r.top - o.top, right: r.right - o.left, bottom: r.bottom - o.top };
      };
      // Stacked (a phone): no lines; a tapped alarm lights up what it watches instead.
      if (mapLayout(widthRef.current ?? el.clientWidth) === "stack") {
        setPaths([]);
        return;
      }
      const drawn = edgesRef.current.flatMap((e) => {
        const a = nodes.current.get(e.from);
        const b = nodes.current.get(e.to);
        const ra = a ? rel(a) : null;
        const rb = b ? rel(b) : null;
        const lane = b?.closest(".svc-lane");
        const laneTop = lane instanceof HTMLElement ? (rel(lane)?.top ?? rb?.top ?? 0) : (rb?.top ?? 0);
        return ra && rb ? [{ ...e, d: edgePath(ra, rb, laneTop - GUTTER) }] : [];
      });
      // Lines to a failing service are drawn last, on top.
      setPaths(drawn.sort((x, y) => Number(x.hot) - Number(y.hot)));
    };
    draw();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(draw);
    ro?.observe(el);
    window.addEventListener("resize", draw);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", draw);
    };
  }, [edgeKey, layout]);

  const keep = (key: string) => (el: HTMLElement | null) => {
    if (el) nodes.current.set(key, el);
    else nodes.current.delete(key);
  };
  const hover = (key: string) => ({
    onMouseEnter: () => setFocus(key),
    onMouseLeave: () => setFocus(null),
    onFocus: () => setFocus(key),
    onBlur: () => setFocus(null),
  });
  const lineClass = (e: Edge) =>
    [e.hot ? "hot" : "", focus === null ? "" : e.from === focus || e.to === focus ? "on" : "dim"]
      .filter(Boolean)
      .join(" ");

  return (
    <section ref={box} className={`svc-map ${layout}`} aria-label="Services map" data-layout={layout}>
      <svg className="svc-lines" aria-hidden="true" data-testid="map-lines">
        {paths.map((p) => (
          <path key={`${p.from}>${p.to}`} d={p.d} className={lineClass(p)} />
        ))}
      </svg>
      <div className={`svc-body${shown.length > 0 ? " watched" : ""}`}>
        {shown.length > 0 && (
          <section aria-label="Alarms and rules" className="svc-lane watch">
            <h3 className="svc-lane-h">Alarms and rules</h3>
            {layout === "stack" && <p className="note svc-hint">Tap one to see what it watches.</p>}
            <ul className="list">
              {shown.map((w) => (
                <li key={watcherKey(w)}>
                  {/* Hover, focus or a tap lights up the lines to what it watches. */}
                  <button
                    type="button"
                    ref={keep(watcherKey(w))}
                    className={`svc-node watcher${lit.has(watcherKey(w)) ? " lit" : ""}`}
                    title={w.name}
                    aria-label={`${w.kind === "alarm" ? "Alarm" : "Rule"} ${w.name}`}
                    aria-pressed={pinned === watcherKey(w)}
                    onClick={() => setPinned((p) => (p === watcherKey(w) ? null : watcherKey(w)))}
                    {...hover(watcherKey(w))}
                  >
                    <span className="svc-kind">{w.kind === "alarm" ? "Alarm" : "Rule"}</span>
                    <span className="svc-name">{w.name}</span>
                  </button>
                </li>
              ))}
              {more > 0 && <li className="note">+{more} more</li>}
            </ul>
          </section>
        )}
        <div className="svc-lanes">
          {lanes.map((l) => (
            <section key={l.runsOn} aria-label={l.title} className="svc-lane" data-runs-on={l.runsOn}>
              <h3 className="svc-lane-h">{l.title}</h3>
              <ul className="list">
                {l.services.map((s) => (
                  <li key={s.name}>
                    <Link
                      ref={keep(serviceKey(s))}
                      to={hrefOf(s)}
                      className={`svc-node ${s.status}${lit.has(serviceKey(s)) ? " lit" : ""}`}
                      aria-label={`${s.name}: ${STATUS_TEXT[s.status]}`}
                      title={s.runtime ?? undefined}
                      {...hover(serviceKey(s))}
                    >
                      <span className={statusDot(s.status)} aria-hidden="true" />
                      <span className="svc-name">{s.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </section>
  );
}
